<template>
    <div class="materials-page">

        <!-- =====================================================
             HERO
        ====================================================== -->

        <section class="materials-hero">

            <div class="hero-decoration hero-decoration-left"></div>
            <div class="hero-decoration hero-decoration-right"></div>

            <div class="container mx-auto px-6 md:px-12">

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

                    <!-- TEXTO -->
                    <div class="hero-copy">

                        <span class="section-label">
                            {{ translate('material for teachers', 'ucfirst') }}
                        </span>

                        <h1 class="f-font-times-new-roman hero-title">
                            {{ translate('less time') }}
                            <span class="italic">{{ translate('planning.') }}</span>
                            <br>
                            {{ translate('more time') }}
                            <span class="italic">{{ translate('teaching.') }}</span>
                        </h1>

                        <p class="hero-description">
                            {{ translate('ready-made, beautiful materials designed to make your classroom routine easier without leaving behind creativity and your way of teaching.') }}
                        </p>

                        <div class="flex flex-wrap gap-4 mt-8">

                            <button
                                class="material-primary-button"
                                @click="scrollToProducts"
                            >
                                {{ translate('explore materials') }}
                            </button>

                            <button
                                class="material-secondary-button"
                                @click="scrollToHowItWorks"
                            >
                                {{ translate('how it works') }}
                            </button>

                        </div>

                    </div>


                    <!-- VISUAL -->
                    <div class="hero-visual">

                        <div class="hero-paper">

                            <div class="paper-top-label">
                                {{ translate('teacher materials') }}
                            </div>

                            <div class="paper-title f-font-times-new-roman">
                                {{ translate('for your') }}
                                <span>{{ translate('routine') }}</span>
                            </div>

                            <div class="paper-lines">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>

                            <div class="paper-sticker">
                                <span>01</span>
                                <small>{{ translate('teach') }}</small>
                            </div>

                            <div class="paper-sticker paper-sticker-second">
                                <span>02</span>
                                <small>{{ translate('organize') }}</small>
                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </section>


        <!-- =====================================================
             MARQUEE
        ====================================================== -->

        <section class="materials-marquee">

            <div class="marquee-track">

                <span v-for="index in 12" :key="index">
                    {{ translate('teacher materials') }}
                    <strong>✦</strong>
                </span>

            </div>

        </section>


        <!-- =====================================================
             PROBLEM
        ====================================================== -->

        <section class="problem-section py-20 md:py-28">

            <div class="container mx-auto px-6 md:px-12">

                <div class="problem-layout">

                    <div class="problem-heading">

                        <span class="section-label">
                            {{ translate('let\'s be honest?') }}
                        </span>

                        <h2 class="f-font-times-new-roman section-title">

                            {{ translate('you didn\'t start') }}
                            <span class="italic">{{ translate('teaching') }}</span>
                            {{ translate('to spend your life searching for material.') }}

                        </h2>

                    </div>


                    <div class="problem-content">

                        <p>
                            {{ translate('between preparing lessons, adapting activities, researching references, and thinking of different ways to engage students, planning can end up taking far more time than it should.') }}
                        </p>

                        <div class="problem-list">

                            <div
                                v-for="(item, index) in problems"
                                :key="index"
                                class="problem-item"
                            >

                                <span class="problem-number f-font-times-new-roman">
                                    0{{ index + 1 }}
                                </span>

                                <div>
                                    <strong>{{ translate(item.title) }}</strong>
                                    <p>{{ translate(item.description) }}</p>
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </section>


        <!-- =====================================================
             BENEFITS
        ====================================================== -->

        <section class="benefits-section">

            <div class="container mx-auto px-6 md:px-12">

                <div class="benefits-header">

                    <span class="section-label section-label-light">
                        {{ translate('real help') }}
                    </span>

                    <h2 class="f-font-times-new-roman section-title-light">
                        {{ translate('the right material changes') }}
                        <span class="italic">{{ translate('the routine.') }}</span>
                    </h2>

                </div>


                <div class="benefits-grid">

                    <article
                        v-for="(benefit, index) in benefits"
                        :key="index"
                        class="benefit-card"
                    >

                        <span class="benefit-number">
                            0{{ index + 1 }}
                        </span>

                        <h3 class="f-font-times-new-roman">
                            {{ translate(benefit.title) }}
                        </h3>

                        <p>
                            {{ translate(benefit.description) }}
                        </p>

                    </article>

                </div>

            </div>

        </section>


        <!-- =====================================================
             PRODUCTS
        ====================================================== -->

        <section
            id="materials-products"
            class="products-section py-20 md:py-28"
        >

            <div class="container mx-auto px-6 md:px-12">

                <div class="text-center max-w-3xl mx-auto mb-14">

                    <span class="section-label">
                        {{ translate('choose yours') }}
                    </span>

                    <h2 class="f-font-times-new-roman section-title mt-5">
                        {{ translate('materials to make your classes') }}
                        <span class="italic">{{ translate('lighter.') }}</span>
                    </h2>

                    <p class="section-description">
                        {{ translate('find materials designed for different moments of your routine. You choose what you need and access the contents available in your package.') }}
                    </p>

                </div>


                <!-- LOADING -->

                <div
                    v-if="loading"
                    class="products-loading"
                >

                    <div
                        v-for="index in 3"
                        :key="index"
                        class="product-skeleton"
                    ></div>

                </div>

                <div
                    v-else-if="sellingMaterialGroups.length > 0"
                    class="products-grid"
                >
                    <article
                        v-for="group in sellingMaterialGroups"
                        :key="group.id"
                        class="product-card"
                    >
                        <div class="product-card-top">
                            <span class="product-tag">
                                {{ getMaterialCountLabel(group) }}
                            </span>

                            <span class="product-number f-font-times-new-roman">
                                {{ formatProductNumber(group) }}
                            </span>
                        </div>

                        <div class="product-card-body">
                            <h3 class="f-font-times-new-roman">
                                {{ group.title }}
                            </h3>

                            <p class="product-description">
                                {{ group.description }}
                            </p>

                            <div
                                v-if="1 != 1 && group.hasSellingMaterials()"
                                class="product-materials"
                            >

                                <div class="materials-heading">
                                    {{ translate('includes:') }}
                                </div>

                                <div
                                    v-for="material in getVisibleMaterials(group)"
                                    :key="material.id"
                                    class="material-row"
                                >

                                    <div class="material-icon" v-if="1 != 1">
                                        {{ getFileIcon(material) }}
                                    </div>

                                    <div class="material-info">

                                        <span class="material-name">
                                            {{ material.name }}
                                        </span>

                                        <span
                                            v-if="material.description"
                                            class="material-description"
                                        >
                                            {{ material.description }}
                                        </span>

                                    </div>

                                </div>

                                <button
                                    v-if="group.sellingMaterials.length > 4"
                                    class="see-more-materials"
                                    @click="toggleMaterials(group.id)"
                                >
                                    {{
                                        expandedGroups.includes(group.id)
                                            ? translate('show less')
                                            : translate('see all {count} materials', '', { count: group.sellingMaterials.length })
                                    }}
                                </button>

                            </div>


                            <div
                                v-if="1 != 1"
                                class="product-empty"
                            >
                                {{ translate('content available after release.') }}
                            </div>
                        </div>

                        <div v-if="group.benefits && Object.keys(group.benefits).length > 0" class="product-card-body">
                            <h2 class="f-font-times-new-roman mb-4 text-2xl">
                                {{ translate('what is included', 'ucfirst') }}:
                            </h2>

                            <ul class="details-list space-y-3" style="max-height: 300px!important; overflow-y: auto!important;">
                                <li 
                                v-for="(value, index) in group.benefits" 
                                :key="index" 
                                class="flex items-start gap-3 text-sm md:text-base text-amber-950/90"
                                >
                                <span class="detail-bullet text-rose-700 font-bold">✦</span>
                                <span class="leading-relaxed">{{ value }}</span>
                                </li>
                            </ul>
                        </div>

                        <div class="product-card-footer">
                            <div>
                                <span class="price-label">
                                    {{ translate('for') }}
                                </span>

                                <div class="product-price">
                                    R$ {{ formatPrice(group.price) }}
                                </div>
                            </div>

                            <button
                                class="product-button"
                                @click="selectGroup(group)"
                            >
                                {{ translate('see more') }}
                            </button>

                        </div>
                    </article>
                </div>

                <div
                    v-else
                    class="empty-products"
                >
                    <p>
                        {{ translate('materials will be available soon.') }}
                    </p>
                </div>

            </div>

        </section>


        <!-- =====================================================
             FOLDER MODULES
        ====================================================== -->

        <section
            id="materials-how-it-works"
            class="modules-section py-20 md:py-28"
        >
            <div class="container mx-auto px-6 md:px-12">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <span class="section-label">
                            {{ translate('organize your routine') }}
                        </span>

                        <h2 class="f-font-times-new-roman section-title mt-5">

                            {{ translate('everything organized in') }}
                            <span class="italic">{{ translate('one place.') }}</span>

                        </h2>

                        <p class="section-description mt-6">
                            {{ translate('each material was designed so you can quickly find what you need without jumping between files, folders, and searches.') }}
                        </p>

                        <button
                            class="material-primary-button mt-8"
                            @click="scrollToProducts"
                        >
                            {{ translate('view materials') }}
                        </button>

                    </div>

                    <div class="audience-folders">
                        <div
                            v-for="(item, index) in folderItems"
                            :key="index"
                            class="audience-folder"
                            :class="[
                                `audience-folder-${index + 1}`,
                                {
                                    'folder-light': index === 1,
                                    'folder-blue': index === 2
                                }
                            ]"
                        >
                            <div class="audience-folder-tab">
                                <span class="f-font-times-new-roman">
                                    0{{ index + 1 }}
                                </span>
                            </div>

                            <div class="audience-folder-body">
                                <span>
                                    {{ translate(item) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- =====================================================
             MATERIAL PREVIEW
        ====================================================== -->
        <section
            v-if="selectedGroup"
            class="preview-section py-20 md:py-28"
        >
            <div class="container mx-auto px-6 md:px-12">
                <div class="preview-header">
                    <span class="section-label">
                        {{ translate('inside the material') }}
                    </span>

                    <h2 class="f-font-times-new-roman section-title">
                        {{ selectedGroup.title }}
                    </h2>
                </div>

                <div class="preview-layout">
                    <div class="preview-material-list">
                        <button
                            v-for="material in selectedGroup.sellingMaterials"
                            :key="material.id"
                            class="preview-material-item"
                            :class="{
                                'preview-material-active':
                                    selectedMaterial?.id === material.id
                            }"
                            @click="previewMaterial(material)"
                        >

                            <span class="material-icon">
                                {{ getFileIcon(material) }}
                            </span>

                            <span class="material-preview-name">
                                {{ material.name }}
                            </span>

                            <span class="preview-arrow">
                                →
                            </span>

                        </button>

                    </div>


                    <div class="preview-viewer">

                        <div
                            v-if="previewLoading"
                            class="preview-loading"
                        >
                            {{ translate('loading material...') }}
                        </div>

                        <template v-else-if="previewUrl">

                            <img
                                v-if="isImage(selectedMaterial)"
                                :src="previewUrl"
                                :alt="selectedMaterial?.name"
                                class="preview-image"
                            />

                            <iframe
                                v-else
                                :src="previewUrl"
                                class="preview-iframe"
                                :title="translate('material preview')"
                            ></iframe>

                        </template>

                        <div
                            v-else
                            class="preview-placeholder"
                        >

                            <span class="f-font-times-new-roman">
                                {{ translate('select a material') }}
                            </span>

                            <p>
                                {{ translate('file preview will appear here.') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- =====================================================
             FINAL CTA
        ====================================================== -->
        <section class="final-cta">
            <div class="container mx-auto px-6 md:px-12">
                <div class="final-cta-inner">
                    <span class="section-label section-label-light">
                        {{ translate('your routine deserves this') }}
                    </span>

                    <h2 class="f-font-times-new-roman">
                        {{ translate('stop spending hours') }}
                        <span class="italic">{{ translate('searching.') }}</span>
                    </h2>

                    <p>
                        {{ translate('choose the materials that make sense for you and make planning a little lighter.') }}
                    </p>

                    <button
                        class="material-light-button"
                        @click="scrollToProducts"
                    >
                        {{ translate('explore materials') }}
                    </button>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>

import { onMounted, onUnmounted, ref } from 'vue';

import SellingMaterialService from '@/services/SellingMaterialService';
import { translate } from '@/composables/translate';
import urlHandler from '@/composables/urlHandler';


// ============================================================
// STATE
// ============================================================

const loading = ref(true);
const sellingMaterialGroups = ref([]);
const selectedGroup = ref(null);
const selectedMaterial = ref(null);
const previewUrl = ref(null);
const previewLoading = ref(false);
const expandedGroups = ref([]);

let objectUrls = [];


// ============================================================
// STATIC CONTENT
// ============================================================

const problems = [
    {
        title: 'wasting precious hours',
        description: 'searching for references, activities, images, and materials for every class.'
    },
    {
        title: 'arriving tired at class',
        description: 'because much of your energy went away before even starting.'
    },
    {
        title: 'repeating the same activities',
        description: 'even knowing that your students could be much more engaged.'
    },
    {
        title: 'leaving what matters for later',
        description: 'because planning everything alone ends up taking up too much space in your routine.'
    }
];


const benefits = [
    {
        title: 'more time',
        description: 'have ready-to-use materials to reduce time spent searching and preparing content.'
    },
    {
        title: 'more organization',
        description: 'find files clearly and enjoy a structure that is easier to use.'
    },
    {
        title: 'more freedom',
        description: 'adapt materials for your students without having to start from scratch.'
    },
    {
        title: 'more confidence',
        description: 'step into the classroom knowing you have a prepared structure to support your lesson.'
    }
];


const folderItems = [
    'ready-to-use materials',
    'activities and visual resources',
    'organized content',
    'current topics and pop culture',
    'more time to teach'
];


// ============================================================
// LIFECYCLE
// ============================================================

onMounted(async () => {

    await loadMaterials();

});


onUnmounted(() => {

    revokeObjectUrls();

});


// ============================================================
// LOAD DATA
// ============================================================

async function loadMaterials() {
    loading.value = true;
    let sellingMaterialGroupsList = [];

    const response = await SellingMaterialService.getGroups(true);

    if (Array.isArray(response) && response.length > 0) {
        sellingMaterialGroupsList = response;
    }

    loading.value = false;
    sellingMaterialGroups.value = sellingMaterialGroupsList;
}

// ============================================================
// PRODUCTS
// ============================================================

function formatPrice(price) {

    if (price === null || price === undefined) {
        return '0,00';
    }

    return Number(price)
        .toFixed(2)
        .replace('.', ',');

}


function formatProductNumber(group) {

    const index =
        sellingMaterialGroups.value.findIndex(
            item => item.id === group.id
        );

    return String(index + 1).padStart(2, '0');

}


function getMaterialCountLabel(group) {

    const count =
        Array.isArray(group.sellingMaterials)
            ? group.sellingMaterials.length
            : 0;

    return count === 1
        ? `1 ${translate('material')}`
        : `${count} ${translate('materials')}`;

}


function getVisibleMaterials(group) {

    const materials =
        Array.isArray(group.sellingMaterials)
            ? group.sellingMaterials
            : [];

    if (expandedGroups.value.includes(group.id)) {
        return materials;
    }

    return materials.slice(0, 4);

}


function toggleMaterials(groupId) {

    if (expandedGroups.value.includes(groupId)) {

        expandedGroups.value =
            expandedGroups.value.filter(
                id => id !== groupId
            );

    } else {

        expandedGroups.value.push(groupId);

    }

}

function selectGroup(group) {
    selectedGroup.value = group;
    selectedMaterial.value = null;
    
    urlHandler.redirectAndScroll(`/teacher-material/${group?.id}`);
}


// ============================================================
// FILES
// ============================================================

function getFileIcon(material) {

    if (!material) {
        return 'FILE';
    }

    const mime =
        material.mime_type || '';

    if (mime.includes('pdf')) {
        return 'PDF';
    }

    if (mime.includes('image')) {
        return 'IMG';
    }

    if (
        mime.includes('word') ||
        mime.includes('document')
    ) {
        return 'DOC';
    }

    if (
        mime.includes('presentation') ||
        mime.includes('powerpoint')
    ) {
        return 'PPT';
    }

    return 'FILE';

}


function isImage(material) {

    return !!(
        material &&
        material.mime_type &&
        material.mime_type.startsWith('image/')
    );

}

function revokeObjectUrls() {

    objectUrls.forEach(
        url => URL.revokeObjectURL(url)
    );

    objectUrls = [];

}


// ============================================================
// NAVIGATION
// ============================================================

function scrollToProducts() {

    document
        .getElementById('materials-products')
        ?.scrollIntoView({
            behavior: 'smooth'
        });

}


function scrollToHowItWorks() {

    document
        .getElementById('materials-how-it-works')
        ?.scrollIntoView({
            behavior: 'smooth'
        });

}

</script>


<style scoped>

/* =========================================================
   ROOT
========================================================= */

.materials-page {
    --material-red: var(--blog-red);
    --material-old-red: var(--blog-old-red);
    --material-orange: var(--orange);
    --material-cream: var(--blog-beige);
    --material-gold: var(--blog-gold);

    background: #fff;
    color: var(--material-old-red);
    overflow: hidden;
}


/* =========================================================
   COMMON
========================================================= */

.container {
    position: relative;
    z-index: 2;
}

.section-label {
    display: inline-block;

    font-family: var(--main-font);

    font-size: 0.72rem;

    font-weight: 700;

    letter-spacing: 0.18em;

    text-transform: uppercase;

    color: var(--material-red);
}

.section-label-light {
    color: var(--material-cream);
}

.section-title {
    font-size: clamp(3rem, 6vw, 5.5rem);

    line-height: 0.95;

    color: var(--material-old-red);

    margin: 0;
}

.section-title-light {
    font-size: clamp(3rem, 6vw, 5.5rem);

    line-height: 0.95;

    color: #fff;

    margin: 0;
}

.section-description {
    max-width: 680px;

    margin-left: auto;
    margin-right: auto;

    color: #666;

    line-height: 1.8;

    font-size: 1rem;
}


/* =========================================================
   HERO
========================================================= */

.materials-hero {
    position: relative;

    min-height: 720px;

    display: flex;
    align-items: center;

    padding: 100px 0;

    background: var(--material-cream);

    overflow: hidden;

    border-top: 1px solid var(--material-gold);
    border-bottom: 1px solid var(--material-gold);
}

.hero-copy {
    position: relative;
    z-index: 3;
}

.hero-title {
    max-width: 760px;

    margin-top: 24px;

    font-size: clamp(4rem, 7vw, 7.5rem);

    line-height: 0.88;

    color: var(--material-old-red);
}

.hero-description {
    max-width: 600px;

    margin-top: 32px;

    color: #604f48;

    font-size: 1.1rem;

    line-height: 1.8;
}

.material-primary-button,
.material-secondary-button,
.material-light-button,
.product-button {
    border: none;

    cursor: pointer;

    transition:
        transform 0.2s ease,
        background-color 0.2s ease;
}

.material-primary-button {
    padding: 15px 26px;

    border-radius: 999px;

    background: var(--material-red);

    color: #fff;

    font-weight: 700;

    text-transform: uppercase;

    font-size: 0.75rem;

    letter-spacing: 0.08em;
}

.material-primary-button:hover {
    transform: translateY(-2px);

    background: var(--material-old-red);
}

.material-secondary-button {
    padding: 14px 25px;

    border-radius: 999px;

    background: transparent;

    border: 1px solid var(--material-red);

    color: var(--material-red);

    font-weight: 700;

    text-transform: uppercase;

    font-size: 0.75rem;

    letter-spacing: 0.08em;
}

.material-secondary-button:hover {
    transform: translateY(-2px);

    background: #fff;
}


/* =========================================================
   HERO PAPER
========================================================= */

.hero-visual {
    min-height: 500px;

    display: flex;

    justify-content: center;

    align-items: center;

    position: relative;
}

.hero-paper {
    position: relative;

    width: min(100%, 430px);

    min-height: 500px;

    padding: 45px;

    background: #fffaf0;

    border: 1px solid var(--material-red);

    box-shadow: 20px 20px 0 rgba(106, 51, 40, 0.12);

    transform: rotate(3deg);
}

.paper-top-label {
    font-size: 0.7rem;

    letter-spacing: 0.18em;

    text-transform: uppercase;

    color: var(--material-red);

    border-bottom: 1px solid var(--material-gold);

    padding-bottom: 12px;
}

.paper-title {
    font-size: 4rem;

    line-height: 0.9;

    margin-top: 55px;

    color: var(--material-old-red);
}

.paper-title span {
    display: block;

    font-style: italic;
}

.paper-lines {
    margin-top: 60px;

    display: flex;

    flex-direction: column;

    gap: 14px;
}

.paper-lines span {
    display: block;

    width: 100%;

    height: 1px;

    background: var(--material-gold);
}

.paper-sticker {
    position: absolute;

    right: -35px;

    top: 150px;

    padding: 15px 22px;

    background: var(--material-red);

    color: #fff;

    transform: rotate(8deg);

    display: flex;

    align-items: center;

    gap: 12px;
}

.paper-sticker span {
    font-family: Georgia, serif;

    font-size: 2rem;
}

.paper-sticker small {
    text-transform: uppercase;

    letter-spacing: 0.1em;

    font-weight: 700;
}

.paper-sticker-second {
    top: auto;

    right: auto;

    left: -35px;

    bottom: 100px;

    background: var(--material-orange);

    transform: rotate(-7deg);
}


/* =========================================================
   HERO DECORATIONS
========================================================= */

.hero-decoration {
    position: absolute;

    width: 180px;

    height: 180px;

    border: 1px solid var(--material-red);

    opacity: 0.12;

    border-radius: 50%;
}

.hero-decoration-left {
    left: -90px;
    bottom: 50px;
}

.hero-decoration-right {
    right: -70px;
    top: 60px;
}


/* =========================================================
   MARQUEE
========================================================= */

.materials-marquee {
    overflow: hidden;

    background: var(--material-red);

    color: #fff;

    padding: 13px 0;

    white-space: nowrap;
}

.marquee-track {
    display: inline-flex;

    animation: materials-marquee 35s linear infinite;
}

.marquee-track span {
    margin-right: 30px;

    font-size: 0.75rem;

    letter-spacing: 0.14em;

    font-weight: 700;
}

.marquee-track strong {
    margin-left: 25px;
}

@keyframes materials-marquee {

    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(-50%);
    }

}


/* =========================================================
   PROBLEM
========================================================= */

.problem-section {
    background: #fff;
}

.problem-layout {
    display: grid;

    grid-template-columns:
        minmax(0, 0.9fr)
        minmax(0, 1.1fr);

    gap: 100px;

    align-items: start;
}

.problem-heading {
    position: sticky;

    top: 100px;
}

.problem-content > p {
    color: #665c59;

    font-size: 1.05rem;

    line-height: 1.8;

    margin-bottom: 40px;
}

.problem-list {
    display: flex;

    flex-direction: column;
}

.problem-item {
    display: grid;

    grid-template-columns: 70px 1fr;

    gap: 20px;

    padding: 25px 0;

    border-top: 1px solid var(--material-gold);
}

.problem-item:last-child {
    border-bottom: 1px solid var(--material-gold);
}

.problem-number {
    font-size: 2.4rem;

    color: var(--material-red);
}

.problem-item strong {
    display: block;

    color: var(--material-old-red);

    font-size: 1rem;

    margin-bottom: 5px;
}

.problem-item p {
    margin: 0;

    color: #777;

    line-height: 1.6;
}


/* =========================================================
   BENEFITS
========================================================= */

.benefits-section {
    padding: 100px 0;

    background: var(--material-red);

    color: #fff;
}

.benefits-header {
    max-width: 800px;

    margin-bottom: 70px;
}

.benefits-grid {
    display: grid;

    grid-template-columns:
        repeat(4, minmax(0, 1fr));

    gap: 1px;

    background: rgba(255,255,255,0.25);

    border: 1px solid rgba(255,255,255,0.25);
}

.benefit-card {
    padding: 35px;

    min-height: 280px;

    background: var(--material-red);

    transition:
        transform 0.25s ease,
        background-color 0.25s ease;
}

.benefit-card:hover {
    transform: translateY(-8px);

    background: var(--material-old-red);
}

.benefit-number {
    font-size: 0.7rem;

    letter-spacing: 0.15em;

    opacity: 0.65;
}

.benefit-card h3 {
    margin-top: 55px;

    font-size: 2rem;

    line-height: 1;
}

.benefit-card p {
    margin-top: 20px;

    color: rgba(255,255,255,0.8);

    line-height: 1.7;
}


/* =========================================================
   PRODUCTS
========================================================= */

.products-section {
    background: var(--material-cream);
}

.products-grid {
    display: grid;

    grid-template-columns:
        repeat(3, minmax(0, 1fr));

    gap: 28px;
}

.product-card {
    display: flex;

    flex-direction: column;

    background: #fffdf7;

    border: 1px solid var(--material-gold);

    min-height: 600px;

    transition:
        transform 0.25s ease,
        box-shadow 0.25s ease;
}

.product-card:hover {
    transform: translateY(-8px);

    box-shadow:
        15px 15px 0 rgba(106, 51, 40, 0.08);
}

.product-card-top {
    display: flex;

    justify-content: space-between;

    align-items: center;

    padding: 22px 25px;

    border-bottom: 1px solid var(--material-gold);
}

.product-tag {
    font-size: 0.65rem;

    text-transform: uppercase;

    letter-spacing: 0.12em;

    color: var(--material-red);
}

.product-number {
    font-size: 2.2rem;

    color: var(--material-red);
}

.product-card-body {
    flex: 1;

    padding: 30px;
}

.product-card-body h3 {
    font-size: 2.3rem;

    line-height: 0.95;

    color: var(--material-old-red);

    margin-bottom: 18px;
}

.product-description {
    color: #777;

    line-height: 1.65;

    font-size: 0.9rem;
}

.product-materials {
    margin-top: 30px;

    border-top: 1px solid var(--material-gold);

    padding-top: 20px;
}

.materials-heading {
    font-size: 0.65rem;

    font-weight: 700;

    text-transform: uppercase;

    letter-spacing: 0.15em;

    color: var(--material-red);

    margin-bottom: 10px;
}

.material-row {
    display: flex;

    gap: 10px;

    align-items: flex-start;

    padding: 10px 0;

    border-bottom: 1px dashed #dfd0c2;
}

.material-icon {
    flex-shrink: 0;

    width: 36px;

    height: 28px;

    display: flex;

    justify-content: center;

    align-items: center;

    border: 1px solid var(--material-red);

    color: var(--material-red);

    font-size: 0.55rem;

    font-weight: 700;
}

.material-info {
    display: flex;

    flex-direction: column;
}

.material-name {
    font-size: 0.78rem;

    color: var(--material-old-red);

    line-height: 1.3;
}

.material-description {
    margin-top: 3px;

    font-size: 0.68rem;

    color: #888;

    line-height: 1.35;
}

.see-more-materials {
    border: none;

    background: transparent;

    color: var(--material-red);

    cursor: pointer;

    margin-top: 12px;

    padding: 0;

    font-size: 0.7rem;

    font-weight: 700;
}

.product-empty {
    margin-top: 30px;

    padding: 20px;

    border: 1px dashed var(--material-gold);

    color: #888;

    font-size: 0.8rem;
}

.product-card-footer {
    display: flex;

    justify-content: space-between;

    align-items: center;

    padding: 25px 30px;

    border-top: 1px solid var(--material-gold);
}

.price-label {
    display: block;

    color: #888;

    font-size: 0.7rem;
}

.product-price {
    font-family: Georgia, serif;

    font-size: 2rem;

    color: var(--material-red);
}

.product-button {
    padding: 13px 20px;

    background: var(--material-red);

    color: #fff;

    border-radius: 999px;

    font-size: 0.7rem;

    font-weight: 700;

    text-transform: uppercase;

    letter-spacing: 0.08em;
}

.product-button:hover {
    background: var(--material-old-red);

    transform: translateY(-2px);
}


/* =========================================================
   FOLDERS / MODULES
========================================================= */

.modules-section {
    background: #fff;
}

.audience-folders {
    width: 100%;

    max-width: 620px;

    margin: 0 auto;

    display: flex;

    flex-direction: column;

    gap: 0;

    padding: 35px 0 45px;

    overflow: visible;
}

.audience-folder {
    --folder-color: var(--material-red);
    --folder-text: #fff;

    position: relative;

    width: 100%;

    height: 78px;

    display: flex;

    align-items: stretch;

    margin-top: -2px;

    filter:
        drop-shadow(0 5px 4px rgba(0,0,0,0.05));

    transition:
        transform 0.25s ease;
}

.audience-folder:hover {
    transform:
        translateX(8px)
        translateY(var(--folder-offset, 0));
}


/*
 * A aba do número.
 *
 * Ela NÃO é um card separado.
 * Ela faz parte da própria pasta.
 */

.audience-folder-tab {
    position: relative;

    width: 105px;

    min-width: 105px;

    height: 78px;

    display: flex;

    align-items: center;

    justify-content: center;

    background: var(--folder-color);

    color: var(--folder-text);

    border-radius:
        12px 0 0 0;

    z-index: 3;
}


/*
 * Corte diagonal que transforma
 * a parte direita da aba em uma
 * pasta/ficha.
 */

.audience-folder-tab::after {
    content: "";

    position: absolute;

    right: -28px;

    top: 0;

    width: 48px;

    height: 78px;

    background: var(--folder-color);

    clip-path:
        polygon(
            0 0,
            55% 0,
            100% 50%,
            55% 100%,
            0 100%,
            45% 50%
        );

    z-index: -1;
}

.audience-folder-tab span {
    font-size: 2.2rem;

    line-height: 1;

    position: relative;

    z-index: 4;
}


/*
 * Corpo principal da pasta.
 */

.audience-folder-body {
    flex: 1;

    height: 78px;

    display: flex;

    align-items: center;

    padding:
        0 42px 0 35px;

    background: var(--folder-color);

    color: var(--folder-text);

    border-radius:
        0 38px 38px 0;

    position: relative;

    z-index: 2;
}

.audience-folder-body span {
    max-width: 400px;

    font-size: 0.9rem;

    font-weight: 600;

    line-height: 1.25;
}


/*
 * Sobreposição real.
 *
 * Cada pasta fica um pouco mais
 * acima da anterior.
 */

.audience-folder-1 {
    --folder-offset: 0px;
    z-index: 5;
}

.audience-folder-2 {
    --folder-offset: -8px;
    z-index: 4;
    transform: translateY(-8px);
}

.audience-folder-3 {
    --folder-offset: -16px;
    z-index: 3;
    transform: translateY(-16px);
}

.audience-folder-4 {
    --folder-offset: -24px;

    z-index: 2;

    transform: translateY(-24px);
}

.audience-folder-5 {
    --folder-offset: -32px;
    z-index: 1;
    transform: translateY(-32px);
}


/*
 * Segunda pasta — creme.
 */

.audience-folder-2 {
    --folder-color: var(--material-cream);
    --folder-text: var(--material-old-red);
}

.audience-folder-2 .audience-folder-tab,
.audience-folder-2 .audience-folder-body {
    border-top: 1px solid var(--material-gold);
    border-bottom: 1px solid var(--material-gold);
}


/*
 * Terceira pasta — azul.
 *
 * Mantive azul apenas como pequeno
 * contraste da referência visual.
 */

.audience-folder-3 {
    --folder-color: var(--orange);
    --folder-text: #fff;
}


/*
 * Quarta e quinta retornam ao vinho.
 */

.audience-folder-4,
.audience-folder-5 {
    --folder-color: var(--material-red);
    --folder-text: #fff;
}


/* =========================================================
   PREVIEW
========================================================= */

.preview-section {
    background: var(--material-cream);
}

.preview-header {
    margin-bottom: 45px;
}

.preview-header .section-title {
    margin-top: 15px;
}

.preview-layout {
    display: grid;

    grid-template-columns:
        360px minmax(0, 1fr);

    gap: 30px;

    min-height: 600px;
}

.preview-material-list {
    background: #fffdf7;

    border: 1px solid var(--material-gold);

    padding: 12px;
}

.preview-material-item {
    width: 100%;

    border: none;

    background: transparent;

    cursor: pointer;

    display: grid;

    grid-template-columns: 40px 1fr 20px;

    align-items: center;

    gap: 10px;

    padding: 15px;

    text-align: left;

    border-bottom: 1px solid #eaded2;

    transition:
        background-color 0.2s ease;
}

.preview-material-item:hover,
.preview-material-active {
    background: var(--material-cream);
}

.material-preview-name {
    color: var(--material-old-red);

    font-size: 0.8rem;

    line-height: 1.3;
}

.preview-arrow {
    color: var(--material-red);
}

.preview-viewer {
    min-height: 600px;

    background: #fff;

    border: 1px solid var(--material-gold);

    display: flex;

    justify-content: center;

    align-items: center;

    overflow: hidden;
}

.preview-iframe {
    width: 100%;

    height: 600px;

    border: none;
}

.preview-image {
    display: block;

    width: 100%;

    height: 600px;

    object-fit: contain;

    padding: 20px;
}

.preview-placeholder,
.preview-loading {
    text-align: center;

    color: #777;

    padding: 40px;
}

.preview-placeholder span {
    display: block;

    font-size: 3rem;

    color: var(--material-old-red);

    margin-bottom: 15px;
}

.preview-placeholder p {
    font-size: 0.85rem;
}


/* =========================================================
   LOADING
========================================================= */

.products-loading {
    display: grid;

    grid-template-columns:
        repeat(3, 1fr);

    gap: 28px;
}

.product-skeleton {
    height: 600px;

    background:
        linear-gradient(
            90deg,
            #eee7dd 25%,
            #f8f4ed 50%,
            #eee7dd 75%
        );

    background-size: 200% 100%;

    animation:
        skeleton-loading 1.4s infinite;
}

@keyframes skeleton-loading {

    from {
        background-position: 200% 0;
    }

    to {
        background-position: -200% 0;
    }

}

.empty-products {
    text-align: center;

    padding: 100px 20px;

    color: #777;
}


/* =========================================================
   FINAL CTA
========================================================= */

.final-cta {
    padding: 110px 0;

    background: var(--material-red);

    color: #fff;

    text-align: center;
}

.final-cta-inner {
    max-width: 850px;

    margin: 0 auto;
}

.final-cta h2 {
    font-size: clamp(4rem, 7vw, 7rem);

    line-height: 0.9;

    margin: 25px 0;
}

.final-cta p {
    max-width: 600px;

    margin: 0 auto;

    color: rgba(255,255,255,0.8);

    line-height: 1.8;
}

.material-light-button {
    margin-top: 35px;

    padding: 16px 28px;

    border-radius: 999px;

    background: var(--material-cream);

    color: var(--material-red);

    font-weight: 700;

    text-transform: uppercase;

    font-size: 0.75rem;

    letter-spacing: 0.08em;
}

.material-light-button:hover {
    transform: translateY(-2px);

    background: #fff;
}


/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 1024px) {

    .problem-layout {
        grid-template-columns: 1fr;

        gap: 50px;
    }

    .problem-heading {
        position: static;
    }

    .benefits-grid {
        grid-template-columns:
            repeat(2, 1fr);
    }

    .products-grid {
        grid-template-columns:
            repeat(2, 1fr);
    }

    .preview-layout {
        grid-template-columns: 1fr;
    }

    .preview-material-list {
        display: grid;

        grid-template-columns:
            repeat(2, 1fr);
    }

}


@media (max-width: 768px) {

    .materials-hero {
        padding: 75px 0;
    }

    .hero-title {
        font-size: 4rem;
    }

    .hero-visual {
        min-height: 400px;
    }

    .hero-paper {
        min-height: 420px;

        padding: 30px;

        width: 90%;
    }

    .paper-title {
        font-size: 3rem;

        margin-top: 40px;
    }

    .benefits-grid {
        grid-template-columns: 1fr;
    }

    .products-grid {
        grid-template-columns: 1fr;
    }

    .products-loading {
        grid-template-columns: 1fr;
    }

    .audience-folders {
        padding-left: 0;

        padding-right: 0;
    }

    .audience-folder {
        height: 70px;
    }

    .audience-folder-tab {
        width: 75px;

        min-width: 75px;

        height: 70px;
    }

    .audience-folder-tab::after {
        height: 70px;

        right: -22px;
    }

    .audience-folder-body {
        height: 70px;

        padding-left: 28px;

        padding-right: 25px;
    }

    .audience-folder-tab span {
        font-size: 1.7rem;
    }

    .audience-folder-body span {
        font-size: 0.75rem;
    }

    .preview-material-list {
        grid-template-columns: 1fr;
    }

    .preview-viewer,
    .preview-iframe,
    .preview-image {
        min-height: 450px;

        height: 450px;
    }

    .final-cta {
        padding: 80px 0;
    }

    .final-cta h2 {
        font-size: 4rem;
    }

}


@media (max-width: 480px) {

    .hero-title {
        font-size: 3.2rem;
    }

    .section-title,
    .section-title-light {
        font-size: 3.2rem;
    }

    .hero-description {
        font-size: 0.95rem;
    }

    .material-primary-button,
    .material-secondary-button {
        width: 100%;
    }

    .hero-paper {
        transform: rotate(2deg);

        width: 92%;
    }

    .paper-sticker {
        right: -15px;
    }

    .paper-sticker-second {
        left: -15px;
    }

    .audience-folder-tab {
        width: 65px;

        min-width: 65px;
    }

    .audience-folder-body {
        padding-left: 20px;

        padding-right: 15px;
    }

    .audience-folder-body span {
        font-size: 0.68rem;
    }
}

</style>